import React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { TrainingRequestsDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  ApproveTrainingRequestDatatableDocument,
  TrainingsTrainingRequestStatusEnum,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RejectModal } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/FooterReview/RejectModal'
import { useRejectTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useRejectTrainingRequest'

interface Props {
  id: number
  status: TrainingsTrainingRequestStatusEnum
  currentApproverIds: number[]
}

export const ActionsDropdown = ({ id, status, currentApproverIds }: Props) => {
  const [showRejectModal, setShowRejectModal] = React.useState<boolean>(false)
  const [approve, { loading: approving }] = useMutation(ApproveTrainingRequestDatatableDocument, {
    extractorFn: (data) => data.trainingsMutations.approveTrainingsTrainingRequest,
    refetchQueries: [TrainingRequestsDatatableDocument],
  })
  const { reject, loading: rejecting } = useRejectTrainingRequest()
  const { company } = useCurrentSession()
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const isApprover = currentApproverIds?.includes(currentAccessId)

  const showApproveOptions = status === 'review' && isApprover

  const onReject = async (reason: string) => {
    const result = await reject({
      id: id,
      companyId: company.id,
      reason: reason,
    })

    const handled = handleResult(result, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('trainings.training_requests.create.wrong') })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.training_requests.create.success_messages.rejected'),
      })

      setShowRejectModal(false)
    }
  }

  const onApprove = async () => {
    const result = await approve({
      variables: {
        id: id,
        companyId: company.id,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('trainings.training_requests.create.wrong') })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.training_requests.create.success_messages.approved'),
      })
    }
  }

  return (
    <>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger asChild disabled={!showApproveOptions}>
          <Button type="secondary" disabled={!showApproveOptions} iconName="ellipsis" />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="end">
          <>
            <PopoverMenu.TouchableOption
              title={i18n.t(
                'trainings.training_requests.requests.datatable.actions_dropdown.approve'
              )}
              onClick={() => onApprove()}
              disabled={approving}
            />
            <PopoverMenu.TouchableOption
              title={i18n.t(
                'trainings.training_requests.requests.datatable.actions_dropdown.reject'
              )}
              onClick={() => setShowRejectModal(true)}
            />
          </>
        </PopoverMenu.Content>
      </PopoverMenu>
      {showRejectModal && (
        <RejectModal
          onReject={onReject}
          setShowRejectedModal={setShowRejectModal}
          rejecting={rejecting}
        />
      )}
    </>
  )
}
